// Pure date rules extracted from PrescriptionDetailsPage.
//
// No Vue, no axios. Every function that depends on "now"/"today" takes it as a
// parameter so callers stay deterministic and testable.

import moment from 'moment'

/**
 * The save payload's date-only serialization (PrescriptionService.save). Client-side
 * identity comparisons (captureFillIdentity) share it so they match what the server stored.
 */
export function formatDateOnly(value: string | Date): string {
    return moment(value).format('YYYY-MM-DD')
}

export interface ClampFillDateInput {
    defaultDate: Date
    effectiveDate: string | Date | null | undefined
    enforceEscriptEffectiveDate: boolean
}

/**
 * Clamp a proposed fill date forward to the escript effective date when the
 * effective date is strictly later (by day). Returns the default untouched when
 * enforcement is off or there is no effective date.
 */
export function clampFillDateToEffective(input: ClampFillDateInput): Date {
    const { defaultDate, effectiveDate, enforceEscriptEffectiveDate } = input
    if (!effectiveDate || !enforceEscriptEffectiveDate) return defaultDate
    const utcEffective = moment.utc(effectiveDate)
    if (!utcEffective.isAfter(moment(defaultDate), 'day')) return defaultDate
    return new Date(utcEffective.year(), utcEffective.month(), utcEffective.date())
}

export interface FillPrintInput {
    fillDate: string | Date | null | undefined
    drugsFilledDate: string | Date | null | undefined
    /** OrderPrescriptions.Printed for this fill. Absent on payloads that do not carry it. */
    labelPrinted?: string | Date | null | undefined
}

/**
 * Whether the label for THIS fill was never printed.
 *
 * Two signals, checked strongest first. LabelPrinted is fill-scoped and stamped
 * server-side on every print path, so any value there settles the question. It is only
 * absent on payloads that do not select it, which is why DrugsFilledDate remains the
 * fallback: that stamp is best-effort (the ZPL path leaves it to a client POST that is
 * swallowed on failure, and reprints skip it), so it can prove a print happened but
 * never that one did not.
 *
 * DrugsFilledDate survives across refills, so a stamp older than the current fill date
 * belongs to an earlier fill and does not count for this one.
 */
export function isFillUnprinted(input: FillPrintInput): boolean {
    const { fillDate, drugsFilledDate, labelPrinted } = input
    if (!fillDate) return false
    if (labelPrinted) return false
    if (!drugsFilledDate) return true
    return localDay(fillDate).isAfter(localDay(drugsFilledDate), 'day')
}

/**
 * Parse a value for calendar-day comparison. DrugsFilledDate is a UTC instant and must be
 * shifted to local before its day is read; a DATE column arrives as bare 'YYYY-MM-DD' with
 * no time or zone, where a UTC→local shift would subtract a day in UTC-negative timezones.
 */
function localDay(value: string | Date): moment.Moment {
    return typeof value === 'string' && !value.includes('T') ? moment(value) : moment.utc(value).local()
}

export interface WrittenDateBlockInput {
    writtenDate: string | Date | null | undefined
    now: Date
    /** RAW config flag — the super-admin composition lives elsewhere. */
    blockFutureWrittenDates: boolean
}

/**
 * Parse a written date in the LOCAL frame. A date-only 'YYYY-MM-DD' string is
 * read as local midnight (not UTC midnight), so the local getters that consume
 * it return the same calendar day the string names — no US-evening off-by-one.
 */
function parseLocalDate(value: string | Date): Date {
    if (value instanceof Date) return value
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (dateOnly) {
        const [, y, m, d] = dateOnly
        return new Date(Number(y), Number(m) - 1, Number(d))
    }
    return new Date(value)
}

/**
 * Whether a future written date should block the form. Compares calendar days in
 * LOCAL time; date-only strings are parsed in the local frame so the comparison
 * agrees with the local calendar regardless of the host time of day.
 */
export function isWrittenDateBlocked(input: WrittenDateBlockInput): boolean {
    const { writtenDate, now, blockFutureWrittenDates } = input
    if (!writtenDate || !blockFutureWrittenDates) return false
    const w = parseLocalDate(writtenDate)
    const compareDate = new Date(w.getFullYear(), w.getMonth(), w.getDate()).setHours(0, 0, 0, 0)
    const todayDate = new Date(now).setHours(0, 0, 0, 0)
    return compareDate > todayDate
}
